if (!customElements.get('pdp-purchase-test')) {
  customElements.define('pdp-purchase-test', class extends HTMLElement {
    async connectedCallback() {
      const connection = this.connection = Symbol()
      await customElements.whenDefined('product-info')
      if (!this.isConnected || this.connection !== connection) return
      this.product = this.closest('product-info')
      if (!this.product) return
      this.refresh = () => {
        if (this.frame) return
        this.frame = requestAnimationFrame(() => {
          this.frame = null
          this.sync()
        })
      }
      this.onToggle = () => {
        if (!this.isEnabled()) return
        const source = this.currentNode('.js-insider-club-upsell')
        const checkbox = source?.querySelector('input')
        if (checkbox) {
          checkbox.click()
        } else if (source?.querySelector('.liquid-pdp__insider.active')) {
          this.product.openMiniCart?.()
        }
        this.refresh()
      }
      this.querySelector('button').addEventListener('click', this.onToggle)
      this.info = this.querySelector('.pdp-purchase-test__details')
      this.onOutsideClick = (event) => {
        if (this.info.open && !event.composedPath().includes(this.info)) this.closeInfo()
      }
      this.onInfoKeydown = (event) => {
        if (event.key !== 'Escape' || !this.info.open) return
        const restoreFocus = this.info.contains(document.activeElement)
        this.closeInfo()
        if (restoreFocus) this.info.querySelector('summary').focus()
      }
      document.addEventListener('click', this.onOutsideClick, true)
      document.addEventListener('keydown', this.onInfoKeydown)
      this.observer = new MutationObserver(this.refresh)
      this.observe()
      this.product.addEventListener('change', this.refresh)
      this.addEventListener('animationstart', this.refresh)
      this.refresh()
    }
    observe() {
      this.observer.observe(this.product, { childList: true, subtree: true, characterData: true, attributes: true })
    }
    closeInfo() {
      if (this.info?.open) this.info.open = false
    }
    isEnabled() {
      return getComputedStyle(this).getPropertyValue('--pdp-purchase-test-enabled').trim() === '1'
        && this.product?.dataset.productClearance !== 'true'
        && ['page', 'popup'].includes(this.product?.dataset.pdpType)
    }
    setupEntryAnimations() {
      if (!window.setupScrollAnimations) return
      const pairs = [
        ['.pdp-purchase-test__price', '.liquid-pdp__price-row'],
        ['.pdp-purchase-test__membership', '.js-insider-club-upsell'],
      ]
      for (const [targetSelector, sourceSelector] of pairs) {
        const target = this.querySelector(targetSelector)
        if (!target || target.hasAttribute('data-animate')) continue
        const source = this.currentNode(sourceSelector)
        target.classList.add('anim-slide-up')
        target.setAttribute('data-animate', '')
        if (source?.dataset.animateDelay) target.dataset.animateDelay = source.dataset.animateDelay
      }
      // Register only while B is active, so OG's initial stagger is unchanged.
      // The shared utility also handles reduced motion and AJAX insertions.
      window.setupScrollAnimations(this)
    }
    restoreControl() {
      this.closeInfo()
      if (!this.product.hasAttribute('data-purchase-test-active') && !this.shippingMarker) return
      this.product.removeAttribute('data-purchase-test-active')
      this.product.querySelectorAll('.pdp-purchase-test__cta').forEach((label) => label.remove())
      if (this.shippingMarker?.isConnected && this.shipping?.isConnected) {
        this.shippingMarker.after(this.shipping)
      }
      this.shippingMarker?.remove()
      this.shippingMarker = null
      this.shipping = null
      this.dataset.ready = 'false'
    }
    currentNode(selector) {
      // viewTransition inserts the replacement before the old node, then
      // keeps the old subtree inline-hidden for 500 ms. Ignore those outgoing
      // subtrees, but not the control elements hidden by the experiment CSS.
      return Array.from(this.product.querySelectorAll(selector)).find((node) => {
        for (let ancestor = node; ancestor && ancestor !== this.product; ancestor = ancestor.parentElement) {
          if (ancestor.style.display === 'none') return false
        }
        return true
      })
    }
    disconnectedCallback() {
      this.closeInfo()
      document.removeEventListener('click', this.onOutsideClick, true)
      document.removeEventListener('keydown', this.onInfoKeydown)
      this.connection = null
      cancelAnimationFrame(this.frame)
      this.frame = null
      this.querySelector('button')?.removeEventListener('click', this.onToggle)
      this.removeEventListener('animationstart', this.refresh)
      this.observer?.disconnect()
      this.product?.removeEventListener('change', this.refresh)
    }
    sync() {
      const p = this.product
      this.observer.disconnect()
      if (!this.isEnabled()) {
        this.restoreControl()
        this.observe()
        return
      }
      const source = this.currentNode('.js-insider-club-upsell')
      const eligible = !!source && !source.classList.contains('optimize-hide')
      const membershipInCart = source?.querySelector('.liquid-pdp__insider.active')
      const selected = eligible && (!!source.querySelector('input:checked') || !!membershipInCart)
      const parse = node => node ? p.extractPrice(node.textContent) : NaN
      const priceBlock = this.currentNode('.pdp-product-price-wrapper')
      const prices = priceBlock?.querySelectorAll('.pdp-product-price-inner > span:not(.visually-hidden):not(.pdp-product-price-promo-name)') || []
      let total = parse(prices[prices.length - 1])
      const code = this.currentNode('manual-discount-code[data-applied="true"] .js-discount-code-price')
      if (code && p.dataset.isWeeklyDeal !== 'true') total = parse(code)
      if (selected) total = Math.round(total * 100 * Number(source.dataset.membershipDiscountMultiplier)) / 100
      const pack = Math.max(1, p.getPackSizeNumber(p.dataset.productPackSize))
      const quantity = Math.max(1, Number(this.currentNode('product-form')?.getQuantityValue?.() || 1))
      const money = cents => p.formatProductMoneyWithoutTrailingZeros(Math.round(cents))
      const compare = Math.round(Number(p.dataset.productCompareAtPrice) * 100)
      const cents = Math.round(total * 100)
      // Fail open to the control if the current price is not yet available.
      if (!Number.isFinite(cents)) {
        this.dataset.ready = 'false'
        p.removeAttribute('data-purchase-test-active')
        this.observe()
        return
      }
      if (Number.isFinite(cents)) {
        this.querySelector('[data-unit]').textContent = money(Math.floor(cents / pack))
        this.querySelector('[data-unit-label]').textContent = pack > 1 ? (p.packSelectors?.dataset.perItemLabel || '/item') : ''
        const strike = this.querySelector('[data-compare]')
        strike.textContent = compare > cents ? money(Math.floor(compare / pack)) : ''
        this.querySelector('[data-total]').textContent = money(cents * quantity)
        this.querySelector('[data-total-wrapper]').hidden = pack * quantity <= 1
      }
      this.querySelector('.pdp-purchase-test__membership').hidden = !eligible
      const membershipButton = this.querySelector('.pdp-purchase-test__toggle')
      this.querySelector('.pdp-purchase-test__track').hidden = !!membershipInCart
      if (membershipInCart) {
        // This is now the original cart shortcut, not an on/off control.
        membershipButton.removeAttribute('aria-pressed')
      } else {
        membershipButton.setAttribute('aria-pressed', String(selected))
      }
      this.querySelector('[data-heading]').textContent = membershipInCart?.querySelector('.liquid-pdp__insider-info-caption__youre-saving')?.textContent.trim() || source?.dataset.testHeading || ''
      this.querySelector('[data-caption]').textContent = source?.dataset.testCaption || ''
      this.querySelector('[data-caption]').hidden = !!membershipInCart
      this.querySelector('[data-details-copy]').textContent = source?.dataset.testDetails || ''
      const shipping = p.querySelector('.liquid-pdp__ui > .cart-summary__expected-shipping, .liquid-pdp__ui > .js-quick-modal-expected-shipping-message')
      if (shipping && shipping !== this.shipping) {
        this.shipping = shipping
        this.shippingMarker = document.createComment('purchase test delivery position')
        shipping.before(this.shippingMarker)
      }
      const builder = p.dataset.pdpType === 'popup'
        ? p.querySelector('.liquid-pdp__ui > .js-product-form')
        : p.querySelector('.liquid-pdp__builder-button-wrapper')
      if (this.shipping && builder && builder.nextElementSibling !== this.shipping) builder.after(this.shipping)
      this.dataset.ready = 'true'
      p.setAttribute('data-purchase-test-active', '')
      this.setupEntryAnimations()
      this.observe()
    }
  })
}
